import { safeHttpsUrl } from './safeUrl';
import type { PublicGallery } from './types';

/**
 * Who the public gallery says presents it (DV-3713). Decided here once, for
 * the gallery's header and for the Share modal's line describing it, so the
 * two cannot disagree.
 */
export interface GalleryBranding {
  /** The root account's name, or null when the gallery names none. */
  accountName: string | null;
  /** The logo the gallery shows in place of the name, or null. A logo that
   *  then fails to load falls back to the name; each renderer does that
   *  itself, since only it sees the load fail. */
  logoUrl: string | null;
  /** A logo is on file but is not one the gallery will show (not https). */
  logoRejected: boolean;
  /** The website the logo links to, or null. */
  website: string | null;
}

/**
 * The gallery's branding, or null when it presents no one (no usable logo and
 * no name). Both URLs are whatever the account has on file, so only https
 * values are used: never a `javascript:` or `data:` value, nor a relative path
 * that would resolve against this origin.
 */
export const galleryBranding = (
  gallery: Pick<PublicGallery, 'accountName' | 'accountLogoUrl' | 'accountWebsite'>
): GalleryBranding | null => {
  const accountName = gallery.accountName || null;
  const logoUrl = safeHttpsUrl(gallery.accountLogoUrl);
  if (accountName === null && logoUrl === null) return null;
  return {
    accountName,
    logoUrl,
    logoRejected: Boolean(gallery.accountLogoUrl) && logoUrl === null,
    website: safeHttpsUrl(gallery.accountWebsite)
  };
};
